import type { Page } from "../CreateNewPage";

import React from "react";

import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import Popover from "@replo/design-system/components/popover/Popover";
import Separator from "@replo/design-system/components/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronDown, Search } from "lucide-react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

import PagePreviewCard, { PagePreviewCardSkeleton } from "./PagePreviewCard";

type PageField = "name" | "pageRoute" | "updatedAt" | "createdAt";
type SortDirection = "asc" | "desc";

type PageOrderState = {
  field: PageField;
  direction: SortDirection;
};

const DEFAULT_PAGE_ORDER: PageOrderState = {
  field: "updatedAt",
  direction: "desc",
};

const pagesSortOptions = [
  { label: "Name", value: "name" as const },
  { label: "Route", value: "pageRoute" as const },
  { label: "Last edited", value: "updatedAt" as const },
  { label: "Created at", value: "createdAt" as const },
];

const alphabeticalSortOrderOptions = [
  { label: "A to Z", value: "asc" as const },
  { label: "Z to A", value: "desc" as const },
];

const chronologicalSortOrderOptions = [
  { label: "Newest First", value: "desc" as const },
  { label: "Oldest First", value: "asc" as const },
];

const sortPages = (order: PageOrderState, pages?: Page[]) => {
  if (!pages) {
    return [];
  }

  return [...pages].sort((a, b) => {
    const { field, direction } = order;
    let comparison = 0;

    switch (field) {
      case "name":
        comparison = (a.name ?? "").localeCompare(b.name ?? "");
        break;
      case "pageRoute":
        comparison = (a.pageRoute ?? "").localeCompare(b.pageRoute ?? "");
        break;
      case "updatedAt":
        comparison =
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
      case "createdAt":
        comparison =
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      default:
        return 0;
    }

    return direction === "asc" ? comparison : -comparison;
  });
};

interface SortOption<T extends string> {
  label: string;
  value: T;
}

interface SortOrderOption {
  label: string;
  value: "asc" | "desc";
}

interface SortDropdownProps<T extends string> {
  selectedField: T;
  selectedDirection: "asc" | "desc";
  onSortChange: (field: T, direction: "asc" | "desc") => void;
  sortOptions: SortOption<T>[];
  getOrderOptions: (field: T) => SortOrderOption[];
  getFieldLabel?: (field: T) => string;
  className?: string;
  contentClassName?: string;
  placeholder?: string;
}

// ToDO (Ben O., 2025-06-26, REPL-17704): Create a reusable design system component for this
function SortDropdown<T extends string>({
  selectedField,
  selectedDirection,
  onSortChange,
  sortOptions,
  getOrderOptions,
  getFieldLabel,
  className,
  contentClassName,
  placeholder = "Sort by",
}: SortDropdownProps<T>) {
  const [isOpen, setIsOpen] = React.useState(false);

  const getValueLabel = () => {
    if (getFieldLabel) {
      return getFieldLabel(selectedField);
    }

    const option = sortOptions.find((opt) => opt.value === selectedField);
    return option?.label ?? placeholder;
  };

  const handleSortChange = (field: T, direction: "asc" | "desc") => {
    onSortChange(field, direction);
  };

  return (
    <Popover.Root isOpen={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <div
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label="Sort options"
          className={className}
        >
          <Button
            variant="secondary"
            size="base"
            startEnhancer={
              selectedDirection === "asc" ? (
                <ArrowUp size={12} />
              ) : (
                <ArrowDown size={12} />
              )
            }
            endEnhancer={<ChevronDown size={12} />}
            UNSAFE_className="min-w-full text-base font-normal bg-sheet"
            titleAlignment="start"
          >
            {getValueLabel()}
          </Button>
        </div>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        side="bottom"
        className="p-2"
        style={{ width: "var(--radix-popover-trigger-width)" }}
        shouldPreventDefaultOnInteractOutside={false}
        hideCloseButton
      >
        <div
          className={twMerge(
            "text-default flex flex-col gap-2 text-sm",
            contentClassName,
          )}
        >
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort By</h3>
            {sortOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                size="sm"
                selected={selectedField === option.value}
                onClick={() =>
                  handleSortChange(option.value, selectedDirection)
                }
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
          <Separator />
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort Order</h3>
            {getOrderOptions(selectedField).map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                size="sm"
                selected={selectedDirection === option.value}
                onClick={() => handleSortChange(selectedField, option.value)}
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

interface ExistingPageMenuProps {
  selectedPage: Page | null;
  setSelectedPage: (page: Page | null) => void;
  funnelId?: string;
}

function ExistingPageMenu({
  selectedPage,
  setSelectedPage,
  funnelId,
}: ExistingPageMenuProps) {
  const projectId = useCurrentProjectId();
  const [searchQueryInputValue, searchQuery, setSearchQuery] =
    useDebouncedState("");
  const [order, setOrder] = React.useState<PageOrderState>(DEFAULT_PAGE_ORDER);

  const { data: projectPages = [] } = trpc.nextJSProject.listPages.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const { data: funnel } = trpc.funnel.find.useQuery(
    projectId && funnelId ? { projectId, funnelId } : skipToken,
  );

  const displayPages = React.useMemo(() => {
    const funnelPageIds = funnel?.funnelSteps.map((step) => step.file?.id);
    const nonFunnelPages = projectPages.filter(
      (page) => !funnelPageIds?.includes(page.id),
    );

    return sortPages(order, nonFunnelPages);
  }, [projectPages, funnel, order]);

  const filteredPages = displayPages.filter((page) => {
    const pageName = page.name?.toLowerCase() ?? "";
    const pageRoute = page.pageRoute?.toLowerCase() ?? "";
    const query = searchQuery.toLowerCase();

    return pageName.includes(query) || pageRoute.includes(query);
  });

  const handlePageClick = (pageId: string) => {
    if (selectedPage?.id === pageId) {
      setSelectedPage(null);
    } else {
      const page = projectPages?.find((p) => p.id === pageId);
      if (page) {
        setSelectedPage(page);
      }
    }
  };

  const handleSortChange = (field: PageField, direction: SortDirection) => {
    setOrder({ field, direction });
  };

  const getOrderOptions = (field: PageField) => {
    return field === "name" || field === "pageRoute"
      ? alphabeticalSortOrderOptions
      : chronologicalSortOrderOptions;
  };

  return (
    <div className="flex flex-col h-full gap-4 no-scrollbar">
      <div className="flex gap-3 w-full">
        <div className="flex-[5]">
          <Input
            startEnhancer={<Search size={12} />}
            placeholder="Search your pages"
            size="base"
            value={searchQueryInputValue}
            onChange={(e) => setSearchQuery(e.target.value)}
            unsafe_inputClassName="text-sm"
          />
        </div>
        <div className="flex-[2] w-full">
          <SortDropdown
            selectedField={order.field}
            selectedDirection={order.direction}
            onSortChange={handleSortChange}
            sortOptions={pagesSortOptions}
            getOrderOptions={getOrderOptions}
            className="min-w-full"
            contentClassName="min-w-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-20">
        {projectPages
          ? filteredPages?.map((page) => (
              <PagePreviewCard
                key={page.id}
                pageData={{
                  route: page.pageRoute ?? "",
                  title: page.name ?? "Page",
                  description: page.pageRoute ?? "",
                }}
                isSelected={selectedPage?.id === page.id}
                onClick={() => handlePageClick(page.id)}
              />
            ))
          : Array.from({ length: 4 }).map((_, index) => (
              <PagePreviewCardSkeleton key={index} />
            ))}
      </div>
    </div>
  );
}

export default ExistingPageMenu;
